<template>
  <a-modal
    v-model:visible="visible"
    :footer="null"
    :closable="true"
    :maskClosable="false"
    width="660px"
    :bodyStyle="{ padding: 0 }"
    :wrapClassName="'update-log-modal-wrap'"
    @cancel="handleClose"
  >
    <template #title>
      <div class="update-log-header">
        <div class="header-overlay">
          <span class="header-title">更新日志</span>
          <span class="header-subtitle">了解系统最新动态与功能更新</span>
        </div>
      </div>
    </template>
    <div class="update-log-body">
      <div
        v-if="loading"
        class="update-log-loading"
      >
        <a-spin tip="加载中..." />
      </div>
      <div
        v-else-if="logGroups.length > 0"
        class="update-log-content"
      >
        <template v-for="group in logGroups" :key="group.id">
          <h2>{{ group.icon }} {{ group.name }}</h2>
          <div
            v-for="item in group.items"
            :key="item.id"
            class="log-item"
            :class="{ 'has-link': !!item.linkUrl }"
            @click="onLogItemClick(item)"
          >
            <p>
              <span
                v-if="item.tag"
                class="update-log-tag"
                :class="'tag-' + (item.tagType || '')"
              >{{ item.tag }}</span>
              <span v-else>{{ item.icon }} </span>
              <strong>{{ item.title }}</strong>
              <span v-if="formatDate(item.createTime || item.updateTime)" class="log-date">
                {{ formatDate(item.createTime || item.updateTime) }}
              </span>
              <span v-if="item.linkText" class="view-detail">{{ item.linkText }}</span>
            </p>
            <p v-if="item.description" class="log-desc">{{ item.description }}</p>
          </div>
        </template>
      </div>
      <a-empty
        v-else
        description="暂无更新日志"
        class="update-log-empty"
      />
    </div>
  </a-modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { getUpdateLog, getUpdateLogList } from '@/api';
import { StorageUtils } from 'xmgoing-utils';

const visible = ref(false);
const loading = ref(false);
const logGroups = ref<any[]>([]);

/** 已打开的窗口引用，用于复用窗口（第一次打开，第二次聚焦） */
const openedWindows = new Map<string, Window>();

/**
 * 获取并展示更新日志
 */
async function checkAndShowUpdateLog(forceShow = false) {
  // 避免未登录时调用
  const sessionId = StorageUtils.getSessionItem('sessionId');
  if (!sessionId) return;
  loading.value = true;
  try {
    const groups = forceShow ? await loadAllLogGroups() : await loadNoticeLogGroups();
    if (groups && groups.length > 0) {
      logGroups.value = groups;
      visible.value = true;
    } else if (forceShow) {
      logGroups.value = [];
      visible.value = true;
    }
  } catch (e) {
    console.warn('[UpdateLog] 获取更新日志失败', e);
  } finally {
    loading.value = false;
  }
}

async function loadNoticeLogGroups(): Promise<any[]> {
  const res = await getUpdateLog();
  const payload = res?.data || res || {};
  if (payload?.hasUpdate === false) {
    return [];
  }
  return filterGroups(payload.groups || []);
}

async function loadAllLogGroups(): Promise<any[]> {
  const res = await getUpdateLogList();
  const groups = res?.data || res || [];
  return filterGroups(groups);
}

function filterGroups(groups: any[]): any[] {
  if (!Array.isArray(groups)) return [];
  return groups.filter((group) => Array.isArray(group.items) && group.items.length > 0);
}

function formatDate(raw: string) {
  if (!raw) return '';
  // 兼容 "2025-07-20 10:30:00" 或 ISO 字符串
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function handleClose() {
  visible.value = false;
}

/**
 * 点击日志条目，如果配置了 linkUrl 则打开对应链接
 * 使用命名窗口：已打开则聚焦，未打开或已关闭则新建窗口
 */
function onLogItemClick(item: any) {
  if (!item.linkUrl) return;

  const windowName = 'updateLog_' + item.id;
  const existingWin = openedWindows.get(windowName);

  // 窗口已存在且未关闭 → 直接聚焦
  if (existingWin && !existingWin.closed) {
    existingWin.focus();
    return;
  }

  // 窗口不存在或已关闭 → 新建窗口
  const newWin = window.open(item.linkUrl, windowName);
  if (newWin) {
    openedWindows.set(windowName, newWin);
  }
}

defineExpose({
  checkAndShowUpdateLog,
});
</script>

<style lang="less" scoped>
.update-log-header {
  background: url('/static/img/update-log-banner.jpg') center top / cover no-repeat;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;

  .header-overlay {
    text-align: center;
  }

  .header-title {
    display: block;
    font-size: 32px;
    font-weight: 700;
    color: #1a3a5c;
    letter-spacing: 4px;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.6);
  }

  .header-subtitle {
    display: block;
    margin-top: 8px;
    font-size: 14px;
    color: #4a6a8a;
    letter-spacing: 1px;
  }
}

.update-log-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 20px 24px;
}

.update-log-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.update-log-content {
  font-size: 14px;
  color: #333;
  line-height: 1.8;

  h2 {
    color: #1890ff;
    margin-top: 20px;
    margin-bottom: 12px;
    font-weight: 600;
    font-size: 18px;
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 8px;
  }

  .log-item {
    padding: 10px 12px;
    margin-bottom: 8px;
    border-radius: 6px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    transition: background 0.2s;

    &.has-link {
      cursor: pointer;

      &:hover {
        background: #f0f7ff;
      }
    }

    p {
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .view-detail {
      color: #1890ff;
      font-size: 13px;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }

    .log-date {
      color: #8c8c8c;
      font-size: 12px;
      font-weight: 400;
      margin-left: 8px;
      white-space: nowrap;
    }

    .log-desc {
      color: #666;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
}

.update-log-empty {
  padding: 40px 0;
}
</style>

<style lang="less">
.update-log-modal-wrap {
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-modal-close-x {
    color: #4a6a8a;
    font-size: 18px;

    &:hover {
      color: #1a3a5c;
    }
  }

  .ant-modal-header {
    padding: 0;
    border-bottom: none;
  }

  .ant-modal-body {
    padding: 0;
  }
}
</style>
